import { Language } from '../types';

export const translations = {
  en: {
    appName: 'MyLife',
    tagline: 'Personal Life & Finance Operating System',
    heroTitle: 'Master Your Life, Finance & Goals in One Place',
    heroSubtitle: 'A privacy-first, lightning-fast personal life management app. Seamlessly track daily expenses, smart notes, to-dos, habits, and visual reports.',
    getStarted: 'Get Started Free',
    liveDemo: 'Try Live Demo',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    logout: 'Log Out',
    demoMode: 'Demo Mode',
    guestNote: 'Try the app instantly with pre-loaded mock data. No registration required.',
    
    // Nav items
    dashboard: 'Dashboard',
    finance: 'Finance',
    tasks: 'Tasks',
    notes: 'Notes',
    goals: 'Goals & Plans',
    resources: 'Resources',
    habits: 'Habits',
    calendar: 'Calendar',
    reports: 'Reports',
    settings: 'Settings',

    // Dashboard
    welcomeBack: 'Welcome back',
    overview: 'Overview',
    todaysIncome: "Today's Income",
    todaysExpense: "Today's Expense",
    netSavings: 'Net Savings',
    totalBalance: 'Total Balance',
    budgetStatus: 'Monthly Budget Status',
    spentOf: 'spent of',
    upcomingTasks: "Today's Tasks",
    pinnedNotes: 'Pinned Notes',
    habitConsistency: 'Habit Consistency',
    quickAdd: 'Quick Add',
    addTransaction: 'Add Transaction',
    addNote: 'Add Note',
    addTask: 'Add Task',
    addHabit: 'Add Habit',
    noTasksToday: 'No pending tasks for today! Great job.',
    noNotesPinned: 'No pinned notes yet.',
    viewAll: 'View All',
    recentActivity: 'Recent Transactions',

    // Finance
    transactions: 'Transactions',
    accounts: 'Accounts & Wallets',
    budgets: 'Budgets',
    debts: 'Debts & Loans',
    recurring: 'Recurring Bills',
    income: 'Income',
    expense: 'Expense',
    transfer: 'Transfer',
    amount: 'Amount',
    category: 'Category',
    account: 'Account',
    date: 'Date',
    note: 'Note / Description',
    paymentMethod: 'Payment Method',
    cash: 'Cash',
    bank: 'Bank Account',
    bkash: 'bKash',
    nagad: 'Nagad',
    card: 'Credit/Debit Card',
    savings: 'Savings',
    newTransaction: 'New Transaction',
    editTransaction: 'Edit Transaction',
    deleteTransaction: 'Delete Transaction',
    categories: 'Categories',
    addCategory: 'Add Category',
    addAccount: 'Add Account',
    setBudget: 'Set Budget',
    budgetLimit: 'Monthly Limit',
    whoIOwe: 'Who I Owe (Payable)',
    whoOwesMe: 'Who Owes Me (Receivable)',
    settled: 'Settled',
    pending: 'Pending',
    markSettled: 'Mark Settled',
    addDebt: 'Add Debt / Loan',
    dueDate: 'Due Date',
    
    // Finance Folders / Workspaces
    folders: 'Folders',
    allFolders: 'All Folders',
    financeFolder: 'Finance Folder',
    personalFolder: 'Personal',
    businessFolder: 'Business',
    organizationFolder: 'Organization',
    newFolder: 'New Folder',
    createFolder: 'Create Folder',
    editFolder: 'Edit Folder',
    deleteFolder: 'Delete Folder',
    folderName: 'Folder Name',
    folderType: 'Folder Type',
    manageFolders: 'Manage Folders',

    // Notes
    allNotes: 'All Notes',
    newNote: 'Create Note',
    editNote: 'Edit Note',
    deleteNote: 'Delete Note',
    noteTitle: 'Note Title',
    noteContent: 'Write with Markdown (e.g. # Heading, - List, **bold**)...',
    folder: 'Folder',
    tags: 'Tags (comma separated)',
    pinNote: 'Pin to top',
    unpinNote: 'Unpin note',
    searchNotes: 'Search notes by title, tag, or content...',
    noNotesFound: 'No notes found.',

    // Tasks
    allTasks: 'All Tasks',
    todayTasks: 'Today',
    upcomingTasksNav: 'Upcoming',
    completedTasks: 'Completed',
    newTask: 'New Task',
    taskTitle: 'What needs to be done?',
    taskDescription: 'Optional description or subtasks...',
    priority: 'Priority',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    recurrence: 'Recurring',
    none: 'None',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    markCompleted: 'Complete',
    markPending: 'Incomplete',
    deleteTask: 'Delete Task',

    // Reports
    financialReports: 'Financial & Activity Reports',
    personalReport: 'Personal Report',
    personalReportSubtitle: 'Monthly daily accountability sheet for Quran, Hadith, Salah, Studies, Dawah & Habits.',
    personalReportSheet: 'Monthly Report Sheet',
    personalAnalytics: 'Personal Analytics & KPIs',
    financialStatements: 'Financial Statements',
    fillTodaysReport: "Fill Today's Report",
    editDayReport: 'Edit Daily Log',
    dateRange: 'Date Range',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    customRange: 'Custom Range',
    totalIncome: 'Total Income',
    totalExpenses: 'Total Expenses',
    netProfit: 'Net Balance',
    spendingByCategory: 'Spending by Category',
    cashflowTrends: 'Cashflow Trends',
    exportReport: 'Export Report',
    exportCsv: 'Export as CSV',
    exportJson: 'Export Backup JSON',
    printReport: 'Print / Save PDF',
    totalSummary: 'Total',
    quranStudy: 'Quran Study (Verses)',
    tilawat: 'Tilawat',
    tafseer: 'Tafseer',
    hadithStudy: 'Hadith Study',
    hadithCount: 'Count',
    salahPrayer: 'Salah / Prayer',
    jamat: 'Jamat',
    qaza: 'Qaza',
    literatureStudy: 'Literature (Pages)',
    generalBooks: 'General',
    syllabusBooks: 'Syllabus',
    academicStudy: 'Academic Study',
    studyHours: 'Hours',
    classesSessions: 'Classes',
    classTotal: 'Held',
    classAttended: 'Attended',
    contactDawah: 'Contacts & Dawah',
    member: 'Member',
    associate: 'Associate',
    worker: 'Worker',
    supporter: 'Supporter',
    friend: 'Friend',
    outreachContacts: 'Special Outreach',
    talentedStudent: 'Talented',
    wellWisher: 'Well-Wisher',
    mukallifContact: 'Contact',
    distribution: 'Distribution',
    distBooks: 'Books',
    distMagazines: 'Magazine',
    distStickers: 'Stickers/Cards',
    distGifts: 'Gift',
    dutiesHours: 'Duties (Hours)',
    dawahDuty: 'Dawah Work',
    otherDuty: 'Other Work',
    socialMedia: 'Social Media (Hrs)',
    newspaper: 'Newspaper',
    exercise: 'Exercise',
    selfAccountability: 'Self-Accountability',
    sleepHrs: 'Sleep (Hrs)',
    remarks: 'Remarks',

    // Calendar
    calendarTitle: 'Calendar & Upcoming Reminders',
    addEvent: 'Add Reminder / Event',
    billsDue: 'Bills Due',
    scheduledTasks: 'Scheduled Tasks',
    debtDeadlines: 'Debt Deadlines',

    // Goals & Plans
    goalsTitle: 'Yearly Goals & Learning Roadmap',
    newGoal: 'Add Yearly Goal / Learning Plan',
    editGoal: 'Edit Goal',
    deleteGoal: 'Delete Goal',
    goalTitle: 'Goal Title',
    goalCategory: 'Category',
    goalStatus: 'Status',
    milestones: 'Key Milestones',
    addMilestone: 'Add Milestone',
    learningResources: 'Resources / Courses / Books',
    targetQuarter: 'Target Quarter / Timeframe',

    // Resources & Links Hub
    resourcesHub: 'Resource & Link Hub',
    resourcesSubtitle: 'Save, organize, bookmark, and quickly access your most critical tools, docs, and links.',
    newResource: 'Add Resource Link',
    editResource: 'Edit Resource',
    deleteResource: 'Delete Resource',
    resourceTitle: 'Resource Title',
    resourceURL: 'Web URL (https://...)',
    resourceCategory: 'Category',
    resourceDescription: 'Description / Notes',
    resourceTags: 'Tags (comma separated)',
    pinnedResources: 'Pinned Resources',
    favoriteResources: 'Favorite Links',
    copyLink: 'Copy URL',
    linkCopied: 'Copied to clipboard!',
    openLink: 'Open in New Tab',
    qrCode: 'Scan QR Code',
    allResources: 'All Links',
    mostVisited: 'Most Visited',
    searchResources: 'Search resources by name, URL, tag or description...',
    noResourcesFound: 'No resources found matching your search.',

    // Habits
    habitTracker: 'Daily Habit Tracker',
    newHabit: 'Create New Habit',
    habitName: 'Habit Name',
    streak: 'Streak',
    daysStreak: 'days streak',
    completionRate: 'Completion Rate',
    markToday: 'Mark Today',
    frequency: 'Frequency',

    // Settings
    profile: 'User Profile',
    name: 'Full Name',
    email: 'Email Address',
    currency: 'Default Currency',
    language: 'Language',
    theme: 'Theme Mode',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    systemMode: 'Follow System',
    dataBackup: 'Data Backup & Restore',
    backupDesc: 'Download all your finances, notes, tasks, and habits in a single portable JSON file.',
    restoreDesc: 'Restore your data from a previously exported JSON backup file.',
    exportData: 'Export Backup',
    importData: 'Import Backup',
    cloudflareStatus: 'Cloudflare D1 Sync Status',
    cloudflareDesc: 'Connected to Cloudflare Workers edge backend. All data is synchronized locally and with Cloudflare D1.',
    resetData: 'Reset All Data (Clear)',
    resetConfirm: 'Are you sure you want to reset all data? This action cannot be undone.',

    // Common
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Search anything (Cmd+K)...',
    close: 'Close',
    success: 'Saved successfully!',
    error: 'An error occurred. Please try again.',
    filter: 'Filter',
    all: 'All',
  },

};

export const currencySymbols: Record<string, string> = {
  USD: '$',
  BDT: '৳',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  CAD: 'C$',
};

export function formatMoney(amount: number, currency: string = 'BDT', _lang: Language = 'en'): string {
  const symbol = currencySymbols[currency] || '৳';
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol} ${formatted}`;
}
